import type { ChatUi } from '@voll-intelligence/genui-contract';
import type { GenUiDispatch } from '../../shared/ui/action.js';
import { useGenUiI18n } from '../../shared/ui/i18n.js';
import type { GenUiEntry } from '../../shared/ui/registry.js';
import { ApprovalRequestCard } from './ui/approval_request.js';
import { ApprovalResultCard } from './ui/approval_result.js';
import { ApprovalBulkResultCard } from './ui/approval_bulk_result.js';

type ApprovalRequestUi = Extract<ChatUi, { kind: 'approval_request' }>;

/**
 * Os dois botões ecoam o MESMO challengeId ESTRUTURADO MAIS a `decision`
 * escolhida e a `justification` opcional via `approval-decision`; o texto é só
 * feedback visual.
 *
 * **Componente e não um lambda no `render` (#2327, mesmo padrão de
 * `bus/bus_offers/index.tsx`).** O eco precisa do locale da sessão, e `render`
 * é uma função de catálogo onde hook não roda. Reusa as mesmas chaves
 * (`approval.textApproveVoucher`/`textDenyVoucher`) que o `approval_stack`
 * (bancada) já usa para o MESMO eco de decisão de um único voucher.
 */
function ApprovalRequestEntry({
  ui,
  note,
  readOnly,
  dispatch,
}: Readonly<{
  ui: ApprovalRequestUi;
  note?: string;
  readOnly?: boolean;
  dispatch: GenUiDispatch;
}>) {
  const { t } = useGenUiI18n();
  return (
    <ApprovalRequestCard
      request={ui.request}
      note={note}
      readOnly={readOnly}
      onDecide={
        readOnly
          ? undefined
          : (decision, justification) => {
              dispatch({
                type: 'approval-decision',
                challengeId: ui.request.challengeId,
                decision,
                ...(justification === undefined ? {} : { justification }),
                text: t(
                  decision === 'approve'
                    ? 'approval.textApproveVoucher'
                    : 'approval.textDenyVoucher',
                ),
                analytics: { actionId: decision },
              });
            }
      }
    />
  );
}

/** Entrada de catálogo do `approval_request` (slot chat). */
export const approvalRequestEntry: GenUiEntry<ApprovalRequestUi> = {
  kind: 'approval_request',
  slot: 'chat',
  policy: {
    // Cada reemissão carrega um challenge NOVO e deve aparecer como card
    // acionável no turno atual — reconciliar por id colapsaria o pedido novo
    // sobre a linha antiga (challenge já resolvido → congelado).
    reconcileById: false,
  },
  render: ({ ui, note, readOnly, dispatch }) => (
    <ApprovalRequestEntry
      ui={ui}
      {...(note ? { note } : {})}
      {...(readOnly ? { readOnly } : {})}
      dispatch={dispatch}
    />
  ),
};

/** Entrada de catálogo do `approval_result` (slot chat). Display-only — sem ação. */
export const approvalResultEntry: GenUiEntry<Extract<ChatUi, { kind: 'approval_result' }>> = {
  kind: 'approval_result',
  slot: 'chat',
  // Cada resultado é um evento próprio na thread — nunca colapsa no anterior.
  policy: { reconcileById: false },
  render: ({ ui, note }) => <ApprovalResultCard result={ui.result} note={note} />,
};

/** Entrada de catálogo do `approval_bulk_result` (slot chat). Display-only — o
 *  resumo parcial (aprovados/falhas) de uma aprovação em lote. */
export const approvalBulkResultEntry: GenUiEntry<
  Extract<ChatUi, { kind: 'approval_bulk_result' }>
> = {
  kind: 'approval_bulk_result',
  slot: 'chat',
  // Cada resultado é um evento próprio na thread — nunca colapsa no anterior.
  policy: { reconcileById: false },
  render: ({ ui, note }) => <ApprovalBulkResultCard result={ui.result} note={note} />,
};
